import {
  BLOCKS_VIEW_NODECHANNEL_MINIMUM_SIZE,
  BLOCKS_VIEW_ROUTINGCHANNEL_MINIMUM_SIZE,
} from '../consts';
import { createRouter } from './router';
import type { Waypoint } from './types';
import {
  BlocksViewGraph,
  BlocksViewLink,
  BlocksViewNode,
  LayoutData,
  NodeLayoutData,
  LinksAggregation,
  LayoutType,
} from '../types';
import { isGroup } from '../viewModel$/util';
import type { Bounds } from '@ardoq/graph';
import { rectHeight, rectWidth } from '../misc/geometry';
import { getNodeMinimumCellSize, setNodeCellRect } from '../visual/node/node';
import {
  getColPositions,
  getNodeLayoutIndex,
  getRowPositions,
  isLayoutSet,
  stripGrid,
} from './util';
import { sum } from 'lodash';
import { getLinkMinimumLabelWidth, setLinkRoute } from '../visual/link';
import { resetGroupSmart } from './yfilesLayout';
import { fixupGroupGrid, resetGroupGrid } from './gridLayout';
import { isShownByLayout } from './treeLinks';

// const lexicalComparator = (a: BlocksViewNode, b: BlocksViewNode) => a.row < b.row ? -1 : a.row > b.row ? 1 : a.col - b.col;

/* strip unused rows and columns from the specified node and its descendency */
export const stripLayout = (rootNode: BlocksViewNode) => {
  const setLayout = (
    u: BlocksViewNode,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ) => {
    u.col = col;
    u.row = row;
    u.colSpan = colSpan;
    u.rowSpan = rowSpan;
  };

  const doit = (node: BlocksViewNode) => {
    if (isGroup(node)) {
      for (const child of node.children) {
        stripLayout(child);
      }

      stripGrid(node, setLayout);
    }
  };

  doit(rootNode);
};

/** force the group's children to be laid out in the specified way even if they're already laid out */
export const resetLayout = (
  node: BlocksViewNode,
  mode: LayoutType,
  recursive: boolean = false
) => {
  if (!node.children?.length) {
    return;
  }

  if (recursive) {
    node.children.forEach(child => resetLayout(child, mode, recursive));
  }

  switch (mode) {
    case LayoutType.Smart:
      resetGroupSmart(node);
      break;

    case LayoutType.Grid:
      resetGroupGrid(node);
      break;
  }
};

enum ContentAlignment {
  beginning,
  middle,
  end,
  justify,
  stretch,
}

const alignContent = (
  contentAlignment: ContentAlignment,
  size: number,
  sizes: number[]
) => {
  const residual = size - sum(sizes);

  if (residual <= 0) {
    return;
  }

  switch (contentAlignment) {
    case ContentAlignment.beginning:
      sizes[sizes.length - 1] += residual;
      break;

    case ContentAlignment.middle:
      sizes[0] += residual / 2;
      sizes[sizes.length - 1] += residual / 2;
      break;

    case ContentAlignment.end:
      sizes[0] += residual;
      break;

    case ContentAlignment.justify:
      if (sizes.length === 3) {
        alignContent(ContentAlignment.stretch, size, sizes);
      } else {
        const n = Math.ceil(sizes.length / 2) - 2;

        for (let i = 2; i <= sizes.length - 2; i += 2) {
          sizes[i] += residual / n;
        }
      }
      break;

    case ContentAlignment.stretch:
      {
        const n = Math.floor(sizes.length / 2);

        for (let i = 1; i < sizes.length; i += 2) {
          sizes[i] += residual / n;
        }
      }

      break;
  }
};

/** repair the group's unlaid out children */
/* this is used when any of a group's children are restored without any layout information */
/* there are numerous possibilites for what to do here */
/* 1. keep the children which *were* restored where they are and add the others at the bottom */
/* 2. decide the whole group is crap and apply the group's preferred layout */
/* 3. keep the children which *were* restored where they are and try to add the new children to a sensible place based on relationships or something */
/* 4. keep the children which were restored where they are and just try to plug any holes */
/* probably many others */
/* for any particular choice, I can guarantee you that there's at least one use-case for which it fails and that, no, the solution is */
/* not to "let the user choose" */
export const fixupLayout = (rootNode: BlocksViewNode) => {
  const doit = (group: BlocksViewNode) => {
    if (isGroup(group)) {
      let fixupRequired = false;

      for (const child of group.children) {
        doit(child);

        fixupRequired = fixupRequired || !isLayoutSet(child);
      }

      if (fixupRequired) {
        // fixup type should depend on the group's layout type but groups don't really have a layout type
        // and in any case the only real (specified) fixup is gridded

        fixupGroupGrid(group);
      }
    }
  };

  doit(rootNode);
};

/** A link the layout already shows, such as a tree's parent/child pair, gets no line. */
const needsRouting = (link: BlocksViewLink): boolean => !isShownByLayout(link);

/** work out world node and link positions in terms of the graph's layout and routing */
export const applyLayout = (
  graph: BlocksViewGraph,
  linksAggregation?: LinksAggregation
) => {
  const uniqueBusId = (link: BlocksViewLink) =>
    `${link.source.id}${link.target.id}${link.modelId}`;

  const getBusId = (link: BlocksViewLink) => {
    switch (linksAggregation) {
      default:
      case 'hide':
        /* should not happen */
        return uniqueBusId(link);

      case null:
      case 'separate':
        return uniqueBusId(link);

      case 'byReferenceType':
        if (!link.globalTypeId) {
          return link.sourceProxy!.modelId!;
        }

        return link.globalTypeId;
    }
  };

  /* linked groups take the larger of each track along the axis they share */
  const shareLinkedSizes = (node: BlocksViewNode) =>
    (['col', 'row'] as const).forEach(axis => {
      const partners = node.alignedWith?.[axis] ?? [];
      if (!partners.length || partners.some(partner => partner.id < node.id)) {
        return;
      }
      const members = [node, ...partners].filter(member => member.open);
      const key = axis === 'col' ? 'colSize' : 'rowSize';
      const sizes = members.flatMap(member => {
        const own = member[key];
        return own ? [own] : [];
      });
      if (sizes.length < 2) return;
      const shared = Array.from(
        { length: Math.max(...sizes.map(({ length }) => length)) },
        (_, index) => Math.max(...sizes.map(own => own[index] ?? 0))
      );
      members.forEach(member => {
        member[key] = [...shared];
      });
    });

  /* get ready for routing */
  const resetGridArrays = (node: BlocksViewNode) => {
    if (isGroup(node) && node.open) {
      let colCnt = 0;
      let rowCnt = 0;

      // should probably be adjusting for negative and empty cols and rows, too

      node.children.forEach(child => {
        resetGridArrays(child);

        colCnt = Math.max(colCnt, child.col + child.colSpan + 1);
        rowCnt = Math.max(rowCnt, child.row + child.rowSpan + 1);
      });

      node.colSize = new Array<number>(colCnt).fill(
        BLOCKS_VIEW_ROUTINGCHANNEL_MINIMUM_SIZE
      );
      for (let col = 1; col < colCnt; col += 2) {
        node.colSize[col] = BLOCKS_VIEW_NODECHANNEL_MINIMUM_SIZE;
      }

      node.rowSize = new Array<number>(rowCnt).fill(
        BLOCKS_VIEW_ROUTINGCHANNEL_MINIMUM_SIZE
      );
      for (let row = 1; row < rowCnt; row += 2) {
        node.rowSize[row] = BLOCKS_VIEW_NODECHANNEL_MINIMUM_SIZE;
      }
    }
  };

  const setSizes = (node: BlocksViewNode) => {
    if (isGroup(node) && node.open) {
      node.children.forEach(setSizes);
      node.children.forEach(shareLinkedSizes);
    }

    if (isGroup(node) && node.open) {
      /* boost node col and row sizes to take into account routing */
      router.adjustSizes(node);

      for (const child of node.children) {
        /* boost col and row sizes to take into account unspanned children */

        if (child.colSpan !== 1 && child.rowSpan !== 1) {
          continue;
        }

        const sz = getNodeMinimumCellSize(child);

        if (child.colSpan === 1) {
          node.colSize![child.col] = Math.max(node.colSize![child.col], sz[0]);
        }

        if (child.rowSpan === 1) {
          node.rowSize![child.row] = Math.max(node.rowSize![child.row], sz[1]);
        }
      }

      for (const child of node.children) {
        /* boost col and row sizes to take into account spanned children */

        if (child.colSpan === 1 && child.rowSpan === 1) {
          continue;
        }

        const sz = getNodeMinimumCellSize(child);

        for (let i = 0; i < child.colSpan && sz[0] > 0; ++i) {
          sz[0] -= node.colSize![child.col + i];
        }

        for (let i = 0; i < child.rowSpan && sz[1] > 0; ++i) {
          sz[1] -= node.rowSize![child.row + i];
        }

        if (sz[0] > 0) {
          const cn = (child.colSpan - 1) / 2; // number of columns to accept the residual

          if (cn <= 0) {
            /* there are no routing channels to take the slop; give it all to the node column */
            node.colSize![child.col] += sz[0];
          } else {
            for (let i = 1; i < child.colSpan; i += 2) {
              node.colSize![child.col + i] += sz[0] / cn;
            }
          }
        }

        if (sz[1] > 0) {
          const rn = (child.rowSpan - 1) / 2;

          if (rn <= 0) {
            node.rowSize![child.row] += sz[1];
          } else {
            for (let i = 1; i < child.rowSpan; i += 2) {
              node.rowSize![child.row + i] += sz[1] / rn;
            }
          }
        }
      }
    } else {
      /* leaf or closed group */
      node.colSize = undefined;
      node.rowSize = undefined;
    }
  };

  const setPositions = (node: BlocksViewNode, cellParam: Bounds | null) => {
    let cell = cellParam;

    if (!cell && node.colSize && node.rowSize) {
      const w = node.colSize.reduce((w, size) => w + size, 0);
      const h = node.rowSize!.reduce((h, size) => h + size, 0);

      cell = [0, 0, w, h];
    }

    if (!cell) {
      cell = [0, 0, node.width, node.height];
    }

    setNodeCellRect(node, cell);

    if (isGroup(node) && node.open) {
      /* the specific alignment should be a setting of some kind, but for the moment we just maintain
      legacy compatability */

      alignContent(
        node.alignedWith?.col.length
          ? ContentAlignment.beginning
          : ContentAlignment.middle,
        rectWidth(node.content!),
        node.colSize!
      );

      alignContent(
        node.alignedWith?.row.length
          ? ContentAlignment.beginning
          : ContentAlignment.middle,
        rectHeight(node.content!),
        node.rowSize!
      );

      const colPos = getColPositions(node);
      const rowPos = getRowPositions(node);

      node.children.forEach(child => {
        setPositions(child, [
          colPos[child.col],
          rowPos[child.row],
          colPos[child.col + child.colSpan],
          rowPos[child.row + child.rowSpan],
        ]);
      });
    }
  };

  const router = createRouter(getBusId);

  /* the graph synthetic root doesn't have a parent to lay it out */

  graph.root.col = 0;
  graph.root.colSpan = 1;
  graph.root.row = 0;
  graph.root.rowSpan = 1;

  /* get ready for laying out */

  resetGridArrays(graph.root);

  if (linksAggregation !== 'hide') {
    /* route links */

    graph.edges.filter(needsRouting).forEach(link => {
      const waypoints: Waypoint[] = router.routeLink(link);
      const group = link.sourceProxy?.parent;

      if (group && group.colSize && group.rowSize && waypoints.length > 1) {
        const wp = waypoints[0];
        const size = getLinkMinimumLabelWidth(link);

        if (wp.μcol === -Infinity) {
          group.colSize[wp.col - 1] = Math.max(group.colSize[wp.col - 1], size);
        }

        if (wp.μcol === +Infinity) {
          group.colSize[wp.col + 1] = Math.max(group.colSize[wp.col + 1], size);
        }

        if (wp.μrow === -Infinity) {
          group.rowSize[wp.row - 1] = Math.max(group.rowSize[wp.row - 1], size);
        }

        if (wp.μrow === +Infinity) {
          group.rowSize[wp.row + 1] = Math.max(group.rowSize[wp.row + 1], size);
        }
      }
    });
  }

  /* adjust node col and row sizes */

  setSizes(graph.root); // initialise node col and row sizes (bottom up)

  /* set node positions */

  setPositions(graph.root, null);

  /* set link routes according to their waypoints */

  graph.edges.forEach(link => {
    const route = needsRouting(link) ? router.route(link) : undefined;

    if (route && route.length > 1) {
      setLinkRoute(link, route);
    } else {
      link.route = undefined;
      link.sourceProxy = undefined;
      link.targetProxy = undefined;
      link.bounds = undefined;
    }
  });
};

export const restoreLayout = (
  rootNode: BlocksViewNode,
  layoutData: LayoutData
) => {
  rootNode.col = 1;
  rootNode.colSpan = 1;
  rootNode.row = 1;
  rootNode.rowSpan = 1;
  rootNode.open = true;

  let isRestored = true;

  const doit = (nodes: BlocksViewNode[]) => {
    for (const node of nodes) {
      const nodeIndex = getNodeLayoutIndex(node);
      const nodeLayoutData: NodeLayoutData | undefined = layoutData[nodeIndex];

      isRestored = setNodeLayoutData(node, nodeLayoutData) && isRestored;

      if (isGroup(node)) {
        doit(node.children);
      }
    }
  };

  if (isGroup(rootNode)) {
    doit(rootNode.children);
  }

  return isRestored;
};

const isValidLayoutData = (nodeLayoutData: NodeLayoutData) => {
  if (!isFinite(nodeLayoutData.col) || nodeLayoutData.col < 0) {
    return false;
  }

  if (!isFinite(nodeLayoutData.colSpan) || nodeLayoutData.colSpan < 1) {
    return false;
  }

  if (!isFinite(nodeLayoutData.row) || nodeLayoutData.row < 0) {
    return false;
  }

  if (!isFinite(nodeLayoutData.rowSpan) || nodeLayoutData.rowSpan < 1) {
    return false;
  }

  return true;
};

export const setNodeLayoutData = (
  node: BlocksViewNode,
  nodeLayoutData: NodeLayoutData | undefined
) => {
  if (nodeLayoutData && isValidLayoutData(nodeLayoutData)) {
    node.col = nodeLayoutData.col;
    node.colSpan = nodeLayoutData.colSpan;
    node.row = nodeLayoutData.row;
    node.rowSpan = nodeLayoutData.rowSpan;
    node.open =
      isGroup(node) && nodeLayoutData.open === undefined
        ? true
        : nodeLayoutData.open;

    return true;
  }

  node.col = NaN;
  node.colSpan = NaN;
  node.row = NaN;
  node.rowSpan = NaN;
  node.open = true;

  return false;
};
